import React, { useEffect, useRef } from 'react';
import {
  X,
  User,
  Briefcase,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Zap,
  Lock,
  ChevronRight,
  UserCheck,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { JobMatchLogo } from '../common/JobMatchLogo';

interface AuthSelectionModalProps {
  // Optional local control if used stand-alone, or context-driven
  isOpen?: boolean;
  mode?: 'login' | 'register';
  onClose?: () => void;
}

export const AuthSelectionModal: React.FC<AuthSelectionModalProps> = ({
  isOpen: propIsOpen,
  mode: propMode,
  onClose: propOnClose,
}) => {
  const {
    authModalState,
    closeAuthModal,
    openAuthModal,
    navigateTo,
    pendingGoogleAuth,
    selectRoleForGoogleUser,
  } = useApp();

  // Prefer props if passed, otherwise use AppContext state
  const isOpen = propIsOpen !== undefined ? propIsOpen : authModalState.isOpen;
  const currentMode = propMode !== undefined ? propMode : authModalState.mode;
  const handleClose = propOnClose || closeAuthModal;

  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const isGoogleRoleSelection = pendingGoogleAuth?.status === 'requires_role_selection';

  const handleSelectRole = (role: 'customer' | 'professional') => {
    if (isGoogleRoleSelection) {
      selectRoleForGoogleUser(role);
      handleClose();
      return;
    }
    handleClose();
    if (role === 'customer') {
      navigateTo('customer_auth', { authMode: currentMode });
    } else {
      navigateTo('pro_auth', { authMode: currentMode });
    }
  };

  const isLogin = !isGoogleRoleSelection && currentMode === 'login';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
        onClick={handleClose}
      />

      {/* Modal Card */}
      <div
        ref={modalRef}
        className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Top Decorative Nigerian Color Line */}
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-600 via-emerald-400 to-teal-600" />

        {/* Modal Header */}
        <div className="px-6 pt-6 pb-4 sm:px-8 sm:pt-8 flex items-start justify-between border-b border-slate-100 dark:border-slate-800">
          <div className="space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <JobMatchLogo variant="compact" size="md" />
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                🇳🇬 Built for Nigeria
              </span>
            </div>
            <h2 id="auth-modal-title" className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              {isGoogleRoleSelection
                ? 'Choose Your Account Type'
                : isLogin
                ? 'Sign in to JobMatch NG'
                : 'Create your account'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {isGoogleRoleSelection
                ? `Google account verified (${pendingGoogleAuth?.googleUser?.email}). Select how you want to use JobMatch NG:`
                : isLogin
                ? 'Select how you want to sign in to access your dashboard'
                : 'Select your account type to get started with JobMatch NG'}
            </p>
          </div>

          <button
            onClick={handleClose}
            className="p-2 -mr-2 -mt-2 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isGoogleRoleSelection ? (
          <div className="px-6 sm:px-8 pt-4">
            <div className="p-3.5 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/70 flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              <div className="text-xs text-emerald-900 dark:text-emerald-200 leading-relaxed">
                <span className="font-bold">Google login successful:</span> Please choose whether you are registering as a{' '}
                <strong>Customer</strong> or a <strong>Professional</strong>. You will complete phone SMS verification on the next step.
              </div>
            </div>
          </div>
        ) : (
          /* Mode Switcher Tabs */
          <div className="px-6 sm:px-8 pt-4 pb-2">
            <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl">
              <button
                onClick={() => openAuthModal('login')}
                className={`flex-1 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                  isLogin
                    ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs ring-1 ring-slate-200 dark:ring-slate-700'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Lock className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Sign In</span>
              </button>
              <button
                onClick={() => openAuthModal('register')}
                className={`flex-1 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                  !isLogin
                    ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs ring-1 ring-slate-200 dark:ring-slate-700'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Sign Up</span>
              </button>
            </div>
          </div>
        )}

        {/* Two Selection Cards */}
        <div className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {/* Card 1: CUSTOMER */}
          <div
            onClick={() => handleSelectRole('customer')}
            className="group relative flex flex-col justify-between p-5 sm:p-6 rounded-2xl bg-gradient-to-b from-white to-slate-50 dark:from-slate-850 dark:to-slate-900 hover:to-emerald-50/40 dark:hover:to-emerald-950/20 border-2 border-slate-200 dark:border-slate-800 hover:border-emerald-500 dark:hover:border-emerald-500 shadow-xs hover:shadow-lg transition-all duration-200 cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-emerald-500"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelectRole('customer');
              }
            }}
          >
            <div>
              {/* Icon & Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 flex items-center justify-center font-bold text-xl group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all shadow-xs">
                  <User className="w-6 h-6" />
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                  Customer
                </span>
              </div>

              {/* Title & Description */}
              <h3 className="text-lg font-extrabold text-slate-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                Customer
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed">
                Find trusted professionals for the job you need done.
              </p>

              {/* Feature Highlights */}
              <ul className="mt-4 space-y-2 text-[11px] text-slate-600 dark:text-slate-300">
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>Post jobs for free & compare quotes</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>NIN-verified artisans in your LGA</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>Paystack escrow payment protection</span>
                </li>
              </ul>
            </div>

            {/* CTA Button inside card */}
            <div className="mt-6 pt-4 border-t border-slate-200/80 dark:border-slate-800">
              <button
                type="button"
                className="w-full py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-xs group-hover:shadow-md transition-all cursor-pointer"
              >
                <span>{isLogin ? 'Sign In as Customer' : 'Sign Up as Customer'}</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>

          {/* Card 2: PROFESSIONAL */}
          <div
            onClick={() => handleSelectRole('professional')}
            className="group relative flex flex-col justify-between p-5 sm:p-6 rounded-2xl bg-gradient-to-b from-white to-slate-50 dark:from-slate-850 dark:to-slate-900 hover:to-slate-100/80 dark:hover:to-slate-800/80 border-2 border-slate-200 dark:border-slate-800 hover:border-slate-800 dark:hover:border-slate-600 shadow-xs hover:shadow-lg transition-all duration-200 cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-slate-800"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelectRole('professional');
              }
            }}
          >
            <div>
              {/* Icon & Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-bold text-xl group-hover:scale-110 group-hover:bg-slate-800 transition-all shadow-xs">
                  <Briefcase className="w-6 h-6 text-emerald-400" />
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-300 dark:border-slate-700">
                  Professional
                </span>
              </div>

              {/* Title & Description */}
              <h3 className="text-lg font-extrabold text-slate-900 dark:text-white group-hover:text-emerald-500 transition-colors">
                Professional
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed">
                Get matched with customers looking for your skills.
              </p>

              {/* Feature Highlights */}
              <ul className="mt-4 space-y-2 text-[11px] text-slate-600 dark:text-slate-300">
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>Receive direct job alerts in your LGA</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>₦0 monthly subscription fees</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>Keep 90% of earnings straight to bank</span>
                </li>
              </ul>
            </div>

            {/* CTA Button inside card */}
            <div className="mt-6 pt-4 border-t border-slate-200/80 dark:border-slate-800">
              <button
                type="button"
                className="w-full py-2.5 px-3 rounded-xl bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-xs group-hover:shadow-md transition-all cursor-pointer"
              >
                <span>{isLogin ? 'Sign In as Professional' : 'Sign Up as Professional'}</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* Modal Footer / Fast Mode Toggle */}
        <div className="px-6 py-4 sm:px-8 bg-slate-50 dark:bg-slate-950/80 border-t border-slate-100 dark:border-slate-800 flex items-center justify-center text-xs">
          <p className="text-slate-500 dark:text-slate-400 text-center">
            {isLogin ? "Don't have a JobMatch NG account yet?" : 'Already have a JobMatch NG account?'}
            <button
              onClick={() => openAuthModal(isLogin ? 'register' : 'login')}
              className="ml-1.5 font-bold text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 underline underline-offset-2 cursor-pointer"
            >
              {isLogin ? 'Create an account' : 'Sign in here'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
